/**
 * @file SortableList.tsx
 * @description Список с перетаскиванием для панелей элементов (phase19.16
 * Р2; uiux-design-r2 §4): плитки, вопросы, объявления, фото галереи, товары
 * вручную. Строка: ⋮⋮ (dnd-kit; на телефоне — ручка 48), сводка, ✕;
 * нажатие раскрывает поля строки. «+ Добавить» скрывается на пределе.
 * У элементов списка нет своих id — ключи держит сам список и переставляет
 * их вместе с элементами.
 */

import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Label } from "../../../ui/label";
import { DND_ACCESSIBILITY_RU } from "../../../../lib/site-builder/dnd-ru";
import { CSS } from "@dnd-kit/utilities";
import { ChevronRight, GripVertical, Plus, X } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import { Button } from "../../../ui/button";
import { cn } from "@/lib/utils";

interface Props<T> {
  label: string;
  items: readonly T[];
  onChange: (items: T[]) => void;
  /** Новый элемент; нет — кнопки «Добавить» нет (добавление снаружи). */
  makeItem?: () => T;
  addLabel?: string;
  max: number;
  /** Сводка строки: «Вопрос: как заварить?». */
  summary: (item: T, index: number) => ReactNode;
  /** Поля раскрытой строки; нет — строка не раскрывается. */
  renderItem?: (item: T, update: (next: T) => void, index: number) => ReactNode;
  /** Поле с замечанием внутри строки — подсветить. */
  invalidAt?: (index: number) => boolean;
}

let keySeq = 0;
const newKey = () => `row-${++keySeq}`;

/** Ключи строк, согласованные с длиной списка. */
function useRowKeys(length: number) {
  const keys = useRef<string[]>([]);
  while (keys.current.length < length) keys.current.push(newKey());
  if (keys.current.length > length) keys.current = keys.current.slice(0, length);
  return keys;
}

function Row({ id, open, onToggle, onRemove, summary, invalid, children, expandable }: {
  id: string;
  open: boolean;
  onToggle: () => void;
  onRemove: () => void;
  summary: ReactNode;
  invalid: boolean;
  children: ReactNode;
  expandable: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn("rounded-lg bg-surface-low", isDragging && "relative z-10 opacity-70 shadow-lg", invalid && "[box-shadow:inset_0_0_0_1px_var(--destructive)]")}
    >
      <div className="flex min-h-12 items-center gap-1 px-1">
        <button type="button" {...attributes} {...listeners} aria-label="Перетащить" className="inline-flex size-10 shrink-0 cursor-grab touch-none items-center justify-center text-on-surface-variant max-lg:size-12">
          <GripVertical aria-hidden className="size-4" />
        </button>
        {/* data-invalid у свёрнутой строки — сюда фокус после неудачного «Сохранить блок» (SB122); у раскрытой — на само поле. */}
        <button type="button" onClick={onToggle} disabled={!expandable} aria-expanded={expandable ? open : undefined} data-invalid={(invalid && expandable && !open) || undefined} className="flex min-h-12 min-w-0 flex-1 items-center gap-1.5 text-left text-sm">
          {expandable ? <ChevronRight aria-hidden className={cn("size-4 shrink-0 text-on-surface-variant transition-transform", open && "rotate-90")} /> : null}
          <span className="min-w-0 truncate">{summary}</span>
        </button>
        <button type="button" onClick={onRemove} aria-label="Убрать" className="inline-flex size-12 shrink-0 items-center justify-center text-on-surface-variant hover:text-destructive">
          <X aria-hidden className="size-4" />
        </button>
      </div>
      {open && expandable ? <div className="flex flex-col gap-3 px-3 pb-3">{children}</div> : null}
    </li>
  );
}

export function SortableList<T>({ label, items, onChange, makeItem, addLabel = "Добавить", max, summary, renderItem, invalidAt }: Props<T>) {
  const keys = useRowKeys(items.length);
  const [open, setOpen] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event: DragEndEvent) {
    const from = keys.current.indexOf(String(event.active.id));
    const to = event.over ? keys.current.indexOf(String(event.over.id)) : -1;
    if (from < 0 || to < 0 || from === to) return;
    keys.current = arrayMove(keys.current, from, to);
    onChange(arrayMove([...items], from, to));
  }

  function remove(index: number) {
    keys.current = keys.current.filter((_, i) => i !== index);
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div className="flex flex-col gap-2">
      <Label>
        {label} <span className="type-caption tabular-nums text-on-surface-variant">{items.length} из {max}</span>
      </Label>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} accessibility={DND_ACCESSIBILITY_RU}>
        <SortableContext items={keys.current} strategy={verticalListSortingStrategy}>
          <ul className="flex flex-col gap-1">
            {items.map((item, index) => {
              const key = keys.current[index] ?? String(index);
              return (
                <Row
                  key={key}
                  id={key}
                  open={open === key}
                  expandable={!!renderItem}
                  invalid={invalidAt?.(index) ?? false}
                  onToggle={() => setOpen(open === key ? null : key)}
                  onRemove={() => remove(index)}
                  summary={summary(item, index)}
                >
                  {renderItem?.(item, (next) => onChange(items.map((x, i) => (i === index ? next : x))), index)}
                </Row>
              );
            })}
          </ul>
        </SortableContext>
      </DndContext>
      {makeItem && items.length < max ? (
        <Button
          variant="outline"
          className="self-start"
          onClick={() => {
            const key = newKey();
            keys.current = [...keys.current, key];
            onChange([...items, makeItem()]);
            setOpen(key);
          }}
        >
          <Plus aria-hidden />
          {addLabel}
        </Button>
      ) : null}
    </div>
  );
}
